import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ADMIN_API_SERVER_URL } from '../tokens/admin-api-server-url.token';
import { ServerBaseService } from '../server-base.service';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { SalesOrderDetails, OrderLine, ProductSearch, SupplierResult } from '@equipmyschool/emsadminweb-models';
import { ProductVariationEMS, ProductSearchResult, FacetsStringResponse } from '../../../@utils';
import { SupplierService } from '../supplier/supplier.service';
import { CommodityService } from '../commodity/commodity.service';

interface OutstandingPfiCount {
  Printed: number;
  Checked: number;
}

@Injectable({ providedIn: 'root' })
export class OrderProcessingService extends ServerBaseService {
  private readonly selectedAdminStorageKey = 'order-processing.selected-admin';
  private _selectedAdmin$ = new BehaviorSubject<number | null>(this.getStoredSelectedAdmin());
  private _orderDetails$ = new BehaviorSubject<SalesOrderDetails | null>(null);
  private _orderLine$ = new BehaviorSubject<OrderLine | null>(null);
  private _productSearchList$ = new BehaviorSubject<ProductSearch[]>([]);

  public selectedAdmin$ = this._selectedAdmin$.asObservable();
  public orderDetails$ = this._orderDetails$.asObservable();
  public orderLine$ = this._orderLine$.asObservable();
  public productSearchList$ = this._productSearchList$.asObservable();

  public get selectedAdmin(): number | null {
    return this._selectedAdmin$.value;
  }

  public set selectedAdmin(adminId: number | null) {
    this._selectedAdmin$.next(adminId);
    if (adminId === null) sessionStorage.removeItem(this.selectedAdminStorageKey);
    else sessionStorage.setItem(this.selectedAdminStorageKey, String(adminId));
  }

  public clearSelectedAdmin(): void {
    this.selectedAdmin = null;
  }

  private getStoredSelectedAdmin(): number | null {
    const value = sessionStorage.getItem(this.selectedAdminStorageKey);
    if (value === null) return null;

    const adminId = Number(value);
    return Number.isFinite(adminId) ? adminId : null;
  }

  constructor(
    private supplierService: SupplierService,
    private commodityService: CommodityService,
    http: HttpClient,
    @Inject(ADMIN_API_SERVER_URL) serverUrl: string
  ) {
    super(http, serverUrl);
  }

   public getLine(lineId: number) {
    if (lineId)
    {
      this.request<OrderLine>('GET', 'OrderProcessing/GetLineDetails', {LineID: lineId})
        .subscribe({
          next: (data) => {
            this._orderLine$.next(data);
            this.searchProducts(data.Product ? [data.Product] : [], [data.Reference], 1.0);
            if (!this._orderDetails$.value || this._orderDetails$.value.SOID !== data.SalesOrder)
              this.getOrderDetails(data.SalesOrder);
          }
        });
    }
    else if (this._orderLine$ && this._orderLine$.value)
    {
      this._productSearchList$.next([]);
      this.getOrderDetails(this._orderDetails$?.value?.SOID ?? 0, true);
    }
   }

   public getOrderDetails(orderId: number, newLine: boolean = false) {
    this.request<SalesOrderDetails>('GET', 'OrderProcessing/GetSalesOrderDetails', {SOID: orderId})
      .subscribe({
        next: (data) => {
          this._orderDetails$.next(data);
          if (newLine) {
            this._orderLine$.next({
              LineID: 0,
              SalesOrder: (data.SOID ?? 0),
              POLine: (data.MaxPOLine ?? 0) + 1,
              Product: 0,
              Reference: '',
              Description: '',
              UnitPrice: 0,
              CustomerDiscount: 0,
              HandlingCharge: 0,
              Quantity: 0,
              Status: 0,
              FinanceStatus: 0
            } as OrderLine)
          }
        }
      });
   }

  public searchProducts(ids: number[], codes: string[], altMult: number) {
    this.request<ProductSearch[]>('GET', 'OrderProcessing/SearchProducts', {Ids: ids, Codes: codes, AltMult: altMult})
      .subscribe((response) => { this._productSearchList$.next(response) });
  }

  public async mergeProducts(page: ProductSearchResult, variants: ProductVariationEMS[]) {
    const supp: SupplierResult = await firstValueFrom(this.supplierService.getSupplierById(parseInt(page.SupplierID)));
    const distributor = supp.Distributor ? await firstValueFrom(this.supplierService.getSupplierById(supp.Distributor)) : null;
    const commodities = await firstValueFrom(this.commodityService.get());

    // turn the new products into ProductSearch objects
    const newProductSearches: ProductSearch[] = variants.filter(v => !this._productSearchList$.value.find(p => p.ProductID === v.ProductID)).map(v => ({
      ProductID: v.ProductID,
      Code: v.SKU,
      Description: (v.Specific?.find(s => s.Name === 'Title') as FacetsStringResponse)?.Values[0]?.Value ?? page.Title,
      UnitCost: v.RRP,
      UnitValue: v.Cost,
      SupplierID: parseInt(page.SupplierID),
      Supplier: supp?.CompanyName ?? '',
      Status: v.EMSProductStatus,
      Discount: v.EMSDiscount,
      CurrencyCode: page.CurrencySymbol,
      Commodity: v.Commodity,
      CommodityType: commodities.find(c => c.CommodityID === v.Commodity)?.Name ?? '',
      Exchange: page.ExchangeRate,
      SupplierStatus: supp?.Preference ?? 0,
      PackSize: v.Unit,
      LastImported: v.LastImported,
      LastUpdated: v.LastUpdated,
      PricingExpiry: v.PricingExpiry,
      Distributor: distributor?.CompanyName ?? '',
    } as ProductSearch));

    this._productSearchList$.next([...this._productSearchList$.value, ...newProductSearches]);
  }

  public getOutstandingPfiCount(lineId: number): OutstandingPfiCount {
    let pfiDets: OutstandingPfiCount = { Printed: 0, Checked: 0 };
    this.request<OutstandingPfiCount>('GET', 'OrderProcessing/GetOutstandingPfiCount', {LineID: lineId})
      .subscribe({
        next: (data) => {
          pfiDets = data;
        }
      });
    return pfiDets;
  }

  public cancelOutstandingPfis(lineId: number, onlyCancelPrinted: boolean) {
    this.request<void>('POST', 'OrderProcessing/CancelOutstandingPfis', undefined, {LineID: lineId, OnlyCancelPrinted: onlyCancelPrinted})
      .subscribe();
  }

  public checkForCountryChange(newProduct: number, oldProduct: ProductSearch | null) {
    this.request<void>('POST', 'OrderProcessing/CheckForCountryChange', undefined, {Product: newProduct, OldProduct: oldProduct})
      .subscribe();
  }

  public async addLine(line: OrderLine, product: ProductSearch | null): Promise<number> {
    return firstValueFrom(this.request<number>('POST', 'OrderProcessing/AddLine', undefined, {Line: line, Product: product}));
  }

  public editLine(line: OrderLine, product: ProductSearch | null) {
    this.request<void>('POST', 'OrderProcessing/EditLine', undefined, {Line: line, Product: product})
      .subscribe();
  }

  public getUnpricedWebOrders(adminId: number) {
    return this.request<any[]>('GET', 'OrderProcessing/UnpricedWebOrders', {AdminID: adminId});
  }

  public getWebOrders(adminId: number) {
    return this.request<any[]>('GET', 'OrderProcessing/WebOrders', {AdminID: adminId});
  }

  public getOrdersForReview(adminId: number) {
    return this.request<any[]>('GET', 'OrderProcessing/OrdersForReview', {AdminID: adminId});
  }

  public getReviewWithClient(adminId: number) {
    return this.request<any[]>('GET', 'OrderProcessing/ReviewWithClient', {AdminID: adminId});
  }

  public getReviewWithSupplier(adminId: number) {
    return this.request<any[]>('GET', 'OrderProcessing/ReviewWithSupplier', {AdminID: adminId});
  }

  public getProFormasAndQuotes(adminId: number) {
    return this.request<any[]>('GET', 'OrderProcessing/ProFormasAndQuotes', {AdminID: adminId});
  }

  public getClientQueries(adminId: number) {
    return this.request<any[]>('GET', 'OrderProcessing/ClientQueries', {AdminID: adminId});
  }

  public changeSalesOrder(lineId: number, newSalesOrder: string) {
    return this.request<SalesOrderDetails>('POST', 'OrderProcessing/ChangeSalesOrder', undefined, {LineID: lineId, NewSalesOrder: newSalesOrder})
      .subscribe({
        next: (data) => {
          this._orderDetails$.next(data);
        }
      });
  }
}
